import React, { useState, useEffect, useRef, useCallback } from 'react';
import Hls from 'hls.js';
import { SimpleAudioFileFragment } from '~/graphqlTypes/SimpleAudioFileFragment';
import { SimpleBlipFragment } from '~/graphqlTypes/SimpleBlipFragment';
import { PlayerControls } from './PlayerControls';
import { SongInfo } from '~/components/EchoChamber/SongInfo';
import { findBestTranscode } from './transcodePicker';
import { BEM, renderClassName } from '@feedfm/feed-util';
import './EchoChamber.scss';
import { useComponentLog } from '~/hooks/useComponentLog';

const bem = new BEM( 'echo-chamber' );

// TODO: add keyboard shortcuts for play/pause, next, previous, and close

export type EchoChamberSong = SimpleAudioFileFragment | SimpleBlipFragment;

export interface EchoChamberProps {
  song: EchoChamberSong;
  wantsToPlay?: boolean | null;

  selectPrevious?: () => void;
  selectNext?: () => void;
  onPauseChange?: ( paused: boolean ) => void;
  onClose?: () => void;
  onWantsToPlayChange?: ( wantsToPlay: boolean ) => void;

  // Setters to keep local state sync with some parent context/state.
  setContextCurrentTime?: ( currentTime: number ) => void;
  setContextIsPlaying?: ( isPlaying: boolean ) => void;
  setContextIsOpen?: ( isOpen: boolean ) => void;

  // TODO: factor out
  stationPreGain?: number;
  addToStation?: ( songId: number ) => void;
}

export const extractSong = ( song: EchoChamberSong ) => {
  const audioFile = song.__typename === 'Blip' ? song.audioFile : song;
  return audioFile;
};

export const EchoChamber: React.FC<EchoChamberProps> = ( {
  song,
  wantsToPlay: initialWantsToPlay,
  selectPrevious,
  selectNext,
  onPauseChange,
  onClose,
  onWantsToPlayChange,
  setContextCurrentTime,
  setContextIsPlaying,
  setContextIsOpen,

  // TODO: factor out
  stationPreGain = -6,
  addToStation,
} ) => {
  const logger = useComponentLog( EchoChamber );
  const [isOpen, setIsOpen] = useState<boolean>( true );
  const [wantsToPlay, directlySetWantsToPlay] = useState<boolean>( initialWantsToPlay || false );

  const setWantsToPlay = useCallback(
    ( newWantsToPlay ) => {
      directlySetWantsToPlay( newWantsToPlay );
      onWantsToPlayChange?.( newWantsToPlay );
    },
    [directlySetWantsToPlay, onWantsToPlayChange],
  );

  const audioRef = useRef<HTMLAudioElement>( null );
  const hlsRef = useRef<Hls | null>( null );

  const [isLoading, setIsLoading] = useState<boolean>( false );
  const [errorMessaging, setErrorMessaging] = useState<string>( '' );
  const [replayGain, setReplayGain] = useState<number>( 0.0 );
  const [currentTime, setCurrentTime] = useState<number>( 0 );
  const [isPlaying, setIsPlaying] = useState<boolean>( audioRef.current !== null && audioRef.current.paused === false );

  // Update isPlaying based on audio events
  useEffect( () => {
    const current = audioRef.current;
    if ( !current ) return;
    const handlePlay = () => setIsPlaying( true );
    const handlePause = () => setIsPlaying( false );
    current.addEventListener( 'play', handlePlay );
    current.addEventListener( 'pause', handlePause );
    return () => {
      current.removeEventListener( 'play', handlePlay );
      current.removeEventListener( 'pause', handlePause );
    };
  }, [setIsPlaying] );

  const playAudio = useCallback( async () => {
    const errorsToIgnore = [
      'The play() request was interrupted by a call to pause().',
      'The play() request was interrupted by a new load request.',
    ];
    setErrorMessaging( '' );

    if ( audioRef.current ) {
      try {
        await audioRef.current.play();
        onPauseChange?.( false );
      } catch ( error: any ) {
        const errorMessageWithLink = error.message;
        const errorHttpsIndex = errorMessageWithLink.indexOf( ' https' );
        const errorMessageWithOutLink =
          errorHttpsIndex === -1 ? errorMessageWithLink : errorMessageWithLink.substring( 0, errorHttpsIndex );
        if ( !errorsToIgnore.includes( errorMessageWithOutLink ) ) {
          // setErrorMessaging( error.message );
          setErrorMessaging( 'Track playback error' );
          console.error( 'Track playback error', error.message );
        } else {
          console.log( 'Ignoring error: ', error.message );
        }
        // TODO: we should be a bit better at trying again, trying other transcodes, updating the parent component that the file might be bad
        // and then possibly requesting new s3 links.
        // we also should update the state to paused.

        // if ( isPlaying ) {
        //   pauseOrPlay();
        // }
      }
    }
  }, [onPauseChange] );

  const pauseAudio = useCallback( () => {
    if ( audioRef.current ) {
      audioRef.current.pause();
      onPauseChange?.( true );
    }
  }, [onPauseChange] );

  /**
   * If wantsToPlay changes from above, update local state. Changes to null / undefined are ignored.
   */
  useEffect( () => {
    if ( typeof initialWantsToPlay === 'boolean' ) {
      directlySetWantsToPlay( initialWantsToPlay );
    }
  }, [initialWantsToPlay, directlySetWantsToPlay] );

  useEffect( () => {
    if ( wantsToPlay ) {
      setIsOpen( true );
    }
  }, [wantsToPlay] );

  useEffect( () => setWantsToPlay( initialWantsToPlay || false ), [initialWantsToPlay, setWantsToPlay] );

  /* Effect for loading and setting up the audio source */
  useEffect( () => {
    if ( audioRef.current && song !== null ) {
      const currentSong = extractSong( song );
      const bestTranscode = currentSong.transcodes?.length ? findBestTranscode( currentSong.transcodes ) : null;
      if ( !bestTranscode ) {
        setErrorMessaging( 'No transcodes available for this track.' );
        return;
      }
      if ( bestTranscode.signedUrl === audioRef.current.src ) {
        return;
      }
      if ( Hls.isSupported() && bestTranscode.encoding === 'hls' && bestTranscode.signedUrl ) {
        if ( hlsRef.current ) {
          hlsRef.current.destroy();
        }
        const hls = new Hls();
        hlsRef.current = hls;
        hls.loadSource( bestTranscode.signedUrl );
        hls.attachMedia( audioRef.current );
        hls.on( Hls.Events.MANIFEST_PARSED, () => {
          if ( wantsToPlay ) {
            audioRef.current?.play();
          }
        } );
        hls.on( Hls.Events.ERROR, ( _, data ) => {
          if ( data.fatal ) {
            switch ( data.type ) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error( 'fatal network error encountered, try to recover' );
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error( 'fatal media error encountered, try to recover' );
                hls.recoverMediaError();
                break;
              default:
                hls.destroy();
                break;
            }
          }
        } );
      } else if ( audioRef.current.canPlayType( 'application/vnd.apple.mpegurl' ) ) {
        audioRef.current.src = bestTranscode.signedUrl || '';
        audioRef.current.addEventListener( 'loadedmetadata', () => {
          if ( wantsToPlay ) {
            audioRef.current?.play();
          }
        } );
      } else {
        audioRef.current.src = bestTranscode.signedUrl || '';
        if ( wantsToPlay ) {
          audioRef.current.play();
        }
      }
      setReplayGain( bestTranscode?.replayGain || 0.0 );
    }
  }, [song, wantsToPlay] );

  useEffect( () => {
    if ( wantsToPlay && audioRef.current && audioRef.current.paused ) {
      playAudio();
    }
    if ( !wantsToPlay && audioRef.current && !audioRef.current.paused ) {
      pauseAudio();
    }
  }, [wantsToPlay, pauseAudio, playAudio] );

  const stop = useCallback( () => {
    setWantsToPlay( false );
    pauseAudio();
  }, [pauseAudio, setWantsToPlay] );

  const togglePlayPause = useCallback( () => setWantsToPlay( !wantsToPlay ), [wantsToPlay, setWantsToPlay] );

  const handleClose = () => {
    if ( isPlaying ) {
      stop();
      if ( audioRef.current ) {
        audioRef.current.currentTime = 0;
      }
    }
    setIsOpen( false );
    onClose?.();
  };

  const audioRefDuration = audioRef?.current?.duration || 0;

  useEffect( () => {
    const onTimeUpdate = () => setCurrentTime( audioRef.current?.currentTime || 0 );
    const onWaiting = () => setIsLoading( true );
    const onCanPlaythrough = () => setIsLoading( false );
    const onError = () => {
      setErrorMessaging( 'Error loading audio file' );
      setIsLoading( false );
    };
    const onEnded = function advanceTrack() {
      if ( selectNext ) {
        selectNext();
      } else {
        stop();
      }
    };
    const { current } = audioRef;
    current?.addEventListener( 'timeupdate', onTimeUpdate );
    current?.addEventListener( 'ended', onEnded );
    current?.addEventListener( 'waiting', onWaiting );
    current?.addEventListener( 'canplaythrough', onCanPlaythrough );
    current?.addEventListener( 'error', onError );
    return () => {
      current?.removeEventListener( 'timeupdate', onTimeUpdate );
      current?.removeEventListener( 'ended', onEnded );
      current?.removeEventListener( 'waiting', onWaiting );
      current?.removeEventListener( 'canplaythrough', onCanPlaythrough );
      current?.removeEventListener( 'error', onError );
    };
  }, [pauseAudio, selectNext, stop, setContextCurrentTime] );

  useEffect( () => {
    setContextIsPlaying?.( isPlaying );
  }, [isPlaying, setContextIsPlaying] );

  useEffect( () => {
    setContextCurrentTime?.( currentTime );
  }, [currentTime, setContextCurrentTime] );

  useEffect( () => {
    setContextIsOpen?.( isOpen );
  }, [isOpen, setContextIsOpen] );

  const setScrub = ( time: number ) => {
    if ( audioRef && audioRef.current ) {
      audioRef.current.currentTime = time;
    } else {
      logger.debug( 'No audioref' );
    }
  };

  const setAudioRefVolume = ( volume: number ) => {
    if ( audioRef && audioRef.current ) {
      audioRef.current.volume = volume;
    } else {
      logger.debug( 'No audioref' );
    }
  };

  const audioRefProps = {
    duration: audioRefDuration,
    currentTime,
    setScrub,
    setAudioRefVolume,
  };

  return (
    <div className={ renderClassName( bem.mod( { visible: isOpen } ) ) }>
      <audio ref={ audioRef } />
      { isLoading && <div className={ renderClassName( bem.el( 'loading-indicator' ) ) }>Loading...</div> }
      { errorMessaging !== '' && <div className={ renderClassName( bem.el( 'error-message' ) ) }>{ errorMessaging }</div> }
      <PlayerControls
        isPlaying={ isPlaying }
        onPlayPause={ togglePlayPause }
        onNext={ selectNext }
        onPrevious={ selectPrevious }
        onClose={ handleClose }
        { ...audioRefProps }
        pregain={ stationPreGain + replayGain }
        // this function needs to change a bit, since i think the button will depend on if it can or cant be added, with messaging
        onAddToStation={ addToStation ? () => addToStation( song.id ) : undefined }
      />
      <SongInfo song={ song } />
    </div>
  );
};
